import { useEffect } from "react"
import { Outlet, useRouterState } from "@tanstack/react-router"
import { ThemeProvider } from "next-themes"

import { TooltipProvider } from "@/components/ui/tooltip"
import { Toaster } from "@/components/ui/sonner"
import { DEFAULT_APP_THEME } from "@/lib/constants"

/** Routes where the auto-injected Sentry "Report a Bug" widget should be hidden: print & public
 * share outputs (it would bake into the downloaded PDF / show on a public link) and pre-auth pages
 * (looks off before sign-in). It stays visible inside the app, including the selection sheet.
 * Hidden via CSS on `#sentry-feedback` since the widget is injected into <body>, outside the React tree. */
function shouldHideFeedbackButton(pathname: string): boolean {
	return (
		pathname.startsWith("/print") ||
		pathname.startsWith("/share") ||
		pathname.startsWith("/q/") ||
		pathname.startsWith("/sign-in") ||
		pathname.startsWith("/sign-up")
	)
}

export function RootLayout() {
	const pathname = useRouterState({ select: (s) => s.location.pathname })
	// Print routes render into a headless Chromium PDF worker (workstream D) and must always be
	// light — next-themes puts `.dark` on <html>, so nothing inside the page can undo it otherwise.
	const forcedTheme = pathname.startsWith("/print") ? "light" : undefined

	// Toggle a root attribute so CSS can hide the Sentry feedback button on select routes.
	const hideFeedback = shouldHideFeedbackButton(pathname)
	useEffect(() => {
		const root = document.documentElement
		if (hideFeedback) root.dataset.hideFeedback = "true"
		else delete root.dataset.hideFeedback
	}, [hideFeedback])

	return (
		<ThemeProvider attribute="class" defaultTheme={DEFAULT_APP_THEME} enableSystem disableTransitionOnChange forcedTheme={forcedTheme}>
			<TooltipProvider delayDuration={300}>
				<Outlet />
				<Toaster position="top-right" richColors />
			</TooltipProvider>
		</ThemeProvider>
	)
}
